import React from 'react';
import { Delta, InfoTip, MetricKey } from '@/components/shared';
import type { ReferralFunnel } from '../api/types';

export interface FunnelStripProps {
  funnel: ReferralFunnel | null;
  /** The same funnel over the previous window of equal length, when there is one. */
  prior?: ReferralFunnel | null;
  /** Whether the page's date window is all-time (both bounds null). Every
   *  label below must say which basis its number uses, and "used in this
   *  period" is simply false when there is no period. */
  isAllTime: boolean;
}

function fmtNum(v: number): string {
  return v.toLocaleString();
}

/** `null` when the denominator is zero or missing — never a bogus "0.0%". */
function fmtRatioPct(numerator: number, denominator: number): string | null {
  if (denominator <= 0) return null;
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

/** One stat tile. Mirrors the friends page's `Stat` so the two analytics pages read as one system. */
function Stat({
  label,
  value,
  hint,
  info,
  delta,
}: {
  label: string;
  value: string;
  hint?: React.ReactNode;
  info?: MetricKey;
  delta?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 px-5 py-4">
      <p className="text-[11px] text-gray-500 font-medium">
        {label}
        {info && <InfoTip metric={info} />}
      </p>
      <p className="mt-1 text-2xl font-semibold text-white tabular-nums tracking-tight flex items-center gap-2 flex-wrap">
        {value}
        {delta}
      </p>
      {hint && <p className="mt-1.5 text-[11px] text-gray-500">{hint}</p>}
    </div>
  );
}

/** The whole `funnel` section failed server-side — named, so it is never read as zero. */
function UnavailableStat({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 px-5 py-4">
      <p className="text-[11px] text-gray-500 font-medium">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-700 tabular-nums">—</p>
      <p className="mt-1.5 text-[11px] text-amber-500/80">Not available</p>
    </div>
  );
}

/** "codes_minted" is NEVER windowed (standing pool size); everything else
 *  here IS. Every label states its own basis explicitly so the reader is
 *  never left to guess whether two numbers on this strip share one: under a
 *  date window it must not silently claim "ever". */
function periodLabel(isAllTime: boolean): string {
  return isAllTime ? 'all-time' : 'in this period';
}

function tileLabels(isAllTime: boolean): string[] {
  const period = periodLabel(isAllTime);
  return [
    'Codes minted (all-time)',
    `Codes used ${period}`,
    `Referrals counted ${period}`,
    `Referrals verified ${period}`,
    `Promoter codes used ${period}`,
  ];
}

/**
 * The funnel strip: codes minted -> codes used -> referrals counted ->
 * referrals verified, with the promoter-only figures shown alongside.
 *
 * `codes_minted` is the standing size of the whole code pool and is NEVER
 * windowed; `codes_ever_used`, the referral counts and the promoter figures
 * all move with the date range. Mixing an all-time number with a windowed
 * one on the same strip without saying so reads as a contradiction (e.g.
 * "1,169 minted / 0 used" under a narrow window) — every label here names
 * its own basis so that never happens.
 *
 * Personal referral codes are auto-minted for every user, so `codes_minted`
 * is essentially the user count — a denominator that means nothing for
 * conversion. `codes_ever_used` is therefore never rendered as a percentage
 * of it (that would read as a catastrophic conversion rate against a made-up
 * base). The promoter ratio, by contrast, IS a meaningful conversion figure
 * and is shown as one.
 */
export function FunnelStrip({ funnel, prior = null, isAllTime }: FunnelStripProps): React.ReactElement {
  const labels = tileLabels(isAllTime);

  if (funnel === null) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {labels.map((label) => (
          <UnavailableStat key={label} label={label} />
        ))}
      </div>
    );
  }

  const period = periodLabel(isAllTime);
  const verifiedPct = fmtRatioPct(funnel.attributions_verified, funnel.attributions_counted);
  const promoterPct = fmtRatioPct(funnel.promoter_codes_ever_used, funnel.promoter_codes_minted);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      <Stat
        label={labels[0]}
        value={fmtNum(funnel.codes_minted)}
        hint="the whole standing pool — personal codes are auto-minted for every user"
      />
      <Stat
        label={labels[1]}
        value={fmtNum(funnel.codes_ever_used)}
        delta={<Delta current={funnel.codes_ever_used} prior={prior?.codes_ever_used} />}
        hint={`not shown as a % of codes minted (all-time) — that denominator is ~= the user count`}
      />
      <Stat
        label={labels[2]}
        value={fmtNum(funnel.attributions_counted)}
        delta={<Delta current={funnel.attributions_counted} prior={prior?.attributions_counted} />}
        hint="signup + settings entries"
      />
      <Stat
        label={labels[3]}
        info="ref_verified_share"
        value={fmtNum(funnel.attributions_verified)}
        delta={<Delta current={funnel.attributions_verified} prior={prior?.attributions_verified} />}
        hint={verifiedPct ? `${verifiedPct} of counted ${period}` : undefined}
      />
      <Stat
        label={labels[4]}
        info="ref_promoter_used"
        value={`${fmtNum(funnel.promoter_codes_ever_used)} of ${fmtNum(funnel.promoter_codes_minted)}`}
        hint={
          promoterPct
            ? `${promoterPct} of all-time promoter codes minted used ${period}`
            : 'promoter codes minted (all-time)'
        }
      />
    </div>
  );
}

export default FunnelStrip;
